<template>
  <q-page>
    <div class="fixed full-width">
      <q-tabs
        v-model="selectedTab"
        inverted
        class="text-primary"
        v-show="showTabs && !qrCode"
      >
        <q-tab name="myScope" icon="person_pin_circle" :label="checkUser" />
        <q-tab
          name="other"
          icon="add_circle_outline"
          @click="resetFilter"
          :label="$t('common.other')"
        />
      </q-tabs>
      <q-input
        :disable="opened"
        borderless
        dense
        v-model="search"
        :debounce="600"
        :hint="searchInputHint"
        :placeholder="$t('common.search')"
        class="q-px-md"
        v-if="selectedTab === 'myScope' && !isLoadingIssues"
      >
        <template v-slot:append>
          <q-icon v-if="search !== ''" name="close" @click="clearSearch" />
        </template>
      </q-input>
    </div>
    <div
      style="height: 100%; position: relative; top: 102px"
      v-if="selectedTab === 'myScope' && isLoadingIssues"
    >
      <div class="row justify-center q-my-md">
        <q-spinner-ios size="30" color="primary" />
        {{
          $t('common.historyLoadingPercentage', {
            percentage: loadingIssuesPercent
          })
        }}
      </div>
    </div>
    <q-list
      v-if="selectedTab === 'myScope'"
      :class="showTabs ? 'scroll root' : 'scroll rootWithoutTabs'"
    >
      <div
        v-if="!isLoadingIssues"
        :style="searchInputHint ? 'padding-top:20px' : ''"
      >
        <q-infinite-scroll
          @load="(index, done) => loadMore(false, done)"
          :offset="250"
        >
          <issues-list
            :issues="initializedIssues"
            :selectedTab="selectedTab"
          ></issues-list>
          <template v-slot:loading>
            <div class="row justify-center q-my-md">
              <q-spinner-ios size="30" color="primary" />
              {{ $t('common.historyLoading') }}
            </div>
          </template>
        </q-infinite-scroll>
        <div v-if="loaded" class="q-pa-md"></div>
        <q-dialog v-model="opened" maximized ref="modalRef">
          <existing-issue
            :currentIssue="currentIssue"
            :issueId="currentIssue._id"
            :shouldScroll="issueNotificationsCount(currentIssue._id)"
          />
        </q-dialog>
        <q-page-scroller
          position="bottom-right"
          :scroll-offset="150"
          :offset="[18, 18]"
        >
          <q-btn round icon="keyboard_arrow_up" color="accent" />
        </q-page-scroller>
      </div>
    </q-list>
    <q-list
      :class="
        showTabs
          ? !qrCode
            ? 'scroll otherRoot'
            : 'scroll '
          : 'scroll rootWithoutTabs'
      "
      v-else
    >
      <filter-issues-history
        v-model:filterValue="filterValue"
        v-model:filterMode="filterMode"
      ></filter-issues-history>
      <q-infinite-scroll
        v-if="filterValue"
        @load="(index, done) => getIssues(filterValue, true, done)"
        :offset="250"
      >
        <issues-list
          :issues="otherIssues"
          :selectedTab="selectedTab"
        ></issues-list>
        <template v-slot:loading>
          <div class="row justify-center q-my-md">
            <q-spinner-ios size="30" color="primary" />
            {{ $t('common.loading') }} ...
          </div>
        </template>
      </q-infinite-scroll>
    </q-list>
  </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import infiniteScroll from 'vue-infinite-scroll'
import ExistingIssue from 'src/components/issues/Existing'
import FilterIssuesHistory from 'src/components/issues/FilterIssuesHistory'
import IssuesList from 'src/components/issues/IssuesByPlace'
import eventBus from 'src/eventBus'
import { buildCriterias } from 'src/utils/buildCriterias'

const LIMIT = 20

export default {
  inject: ['$DB'],
  directives: { infiniteScroll },
  components: { ExistingIssue, FilterIssuesHistory, IssuesList },
  created() {
    eventBus.$on('localIssueChange', this.reflectIssueChanges)
    eventBus.$on('openModal', this.openModal)
    eventBus.$on('closeIssueModal', this.closeModal)
  },
  async mounted() {
    if ('id' in this.$route.params) {
      this.openModal(this.$route.params.id)
    }
    if ('location' in this.$route.query) {
      this.selectedTab = 'other'
    }

    if (this.$q.platform.is.capacitor) {
      const { Keyboard } = await import('Capacitor/@capacitor/keyboard')
      const mode = this.$q.platform.is.ios ? 'native' : 'body'
      await Keyboard.setResizeMode({ mode })
    }
  },
  async beforeUnmount() {
    eventBus.$off('localIssueChange', this.reflectIssueChanges)
    eventBus.$off('openModal', this.openModal)
    eventBus.$off('closeIssueModal', this.closeModal)

    if (this.$q.platform.is.capacitor) {
      const { Keyboard } = await import('Capacitor/@capacitor/keyboard')
      await Keyboard.setResizeMode({ mode: 'native' })
    }
  },
  data() {
    return {
      selectedTab: 'myScope',
      issues: [],
      currentIssue: null,
      opened: false,
      options: { limit: LIMIT, skip: 0 },
      loaded: false,
      otherLoaded: false,
      search: '',
      inputFillCancelled: false,
      filterValue: null,
      filterMode: '',
      otherIssues: [],
      searchInputStyle: ''
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/attributes',
      categories: 'company/categories',
      notifications: 'reporter/notifications',
      isLoadingIssues: 'reporter/isLoadingIssues',
      loadingIssuesPercent: 'reporter/loadingIssuesPercent',
      qrCode: 'reporter/qrCode'
    }),
    initializedIssues() {
      // Show all issues in current_user's scopes except the private ones authored by another user
      return this.issues.sort((a, b) => {
        const aTime = new Date(a.meta.updatedAt).getTime()
        const bTime = new Date(b.meta.updatedAt).getTime()
        return bTime - aTime
      })
    },
    searchInputHint() {
      if (this.search.length && this.search.length < 3)
        return this.$t('common.searchWarning')
      if (!this.issues.length && this.search.length > 2)
        return this.$t('common.searchError')
      return null
    },
    checkUser() {
      const DECODED_TOKEN = this.$helpers.decodeToken(
        this.$store.getters['reporter/token']
      )

      return DECODED_TOKEN.roles?.includes('reporter')
        ? this.$t('common.myScope')
        : this.$t('common.myIssue')
    },
    showTabs() {
      return this.company.showUnscopedHistory
    }
  },
  methods: {
    clearSearch() {
      this.search = ''
    },
    resetFilter() {
      this.filterValue = null
      this.filterMode = ''
      this.options = { limit: LIMIT, skip: 0 }
    },
    openModal(issueId) {
      if ((this.selectedTab = 'myScope')) {
        this.$DB
          .localIssues()
          .get(String(issueId))
          .then((doc) => {
            this.currentIssue = doc
            this.opened = true
            eventBus.$emit('modalOpened')
          })
          .catch(() => {
            throw 'localIssue cannot be found'
          })
      } else {
        this.currentIssue = this.otherIssues.find(
          (issue) => Number(issue.id) === Number(issueId)
        )
        this.opened = true
        eventBus.$emit('modalOpened')
      }
    },
    closeModal() {
      if (this.opened) {
        this.opened = false
      }
      if (this.search.length !== 0) {
        this.filterIssues(this.search)
      }
      this.currentIssue = null
    },
    initIssues() {
      this.loadMore(true)
    },
    reflectIssueChanges(incommingChange) {
      if (this.isLoadingIssues) return
      var issueExists = false
      this.issues.forEach((issue, i) => {
        if (issue._id === incommingChange.id) {
          // TODO: Notify user that the issue was updated ?
          issueExists = true
          issue.tracking = incommingChange.doc.tracking
          issue.meta = incommingChange.doc.meta
          this.issues = this.issues.filter(
            (el) => el._id !== incommingChange.id
          )
          this.issues.unshift(issue)
        }
      })
      if (!issueExists) {
        // TODO: Notify user that a new issue was added ?
        this.issues.unshift(incommingChange.doc)
      }
      if (this.search.length !== 0) {
        this.filterIssues(this.search)
      }
    },
    resetIssues() {
      this.issues = []
      this.loaded = false
      this.options = { limit: LIMIT, skip: 0 }
    },
    async loadMore(reset = false, done) {
      var self = this
      if (reset) {
        this.resetIssues()
      }
      if (!this.loaded) {
        const criterias = buildCriterias(this.search, this.notifications)
        await this.$DB
          .localIssues()
          .find({
            selector: criterias,
            sort: [{ 'meta.updatedAt': 'desc' }],
            skip: this.options.skip,
            limit: this.options.limit
          })
          .then((response) => {
            if (response.docs.length > 0) {
              // There are some issues loaded
              this.options.skip += this.options.limit
              response.docs.forEach((row) => {
                if (!self.issues.some((issue) => issue._id === row._id)) {
                  self.issues.push(row)
                }
              })
            } else {
              // There is no (more) issue to load
              this.loaded = true
            }
            if (done) {
              done()
            }
          })
          .catch((err) => {
            console.error('loadMore', err)
            if (done) {
              done()
            }
          })
      } else {
        if (done) {
          done()
        }
      }
    },
    issueCategory(issueId) {
      let cat = { img: null }
      this.categories.forEach((category) => {
        category.childs.forEach((subcat) => {
          if (subcat.id === issueId) {
            cat = subcat
          }
        })
      })
      return cat
    },
    issueNotificationsCount(issueId) {
      return this.notifications.filter(
        (notif) => parseInt(notif.id) === parseInt(issueId)
      ).length
    },
    translateStatus(status) {
      return this.$helpers.statusTranslation(status)
    },
    filterIssues(term) {
      this.options.skip = 0

      if (term.length > 2) {
        const criterias = buildCriterias(term, this.notifications)
        this.$DB
          .localIssues()
          .find({
            selector: criterias,
            sort: [{ 'meta.updatedAt': 'desc' }],
            skip: this.options.skip,
            limit: this.options.limit
          })
          .then((results) => {
            console.log('results.docs', {
              'results.docs': results.docs.length,
              skip: this.options.skip,
              limit: this.options.limit
            })
            this.issues = results.docs
          })
      } else if (term.length === 0) {
        // NOTE: No terms or search reseted
        this.initIssues()
      } else if (this.issues.length === 0) {
        // NOTE: Issues not loaded
        this.resetIssues()
      }
    },
    async getIssuesByPlaceId(placeId, loadMore = false, done) {
      if (!loadMore) {
        this.otherLoaded = false
      }

      if (!this.otherLoaded) {
        if (!loadMore) {
          this.$q.loading.show()
        }
        return this.$SowellProxy
          .issuesByPlaceId(placeId, this.options.skip, this.options.limit)
          .then((response) => {
            this.handleIssuesResponse(response, loadMore)
          })
          .catch((err) => {
            console.log(err)
          })
          .finally(() => {
            if (!loadMore) {
              this.$q.loading.hide()
            }
            if (done) {
              done()
            }
          })
      } else {
        if (done) {
          done()
        }
      }
    },
    async getIssuesByResidenceId(residenceId, loadMore = false, done) {
      if (!loadMore) {
        this.otherLoaded = false
      }

      if (!this.otherLoaded) {
        if (!loadMore) {
          this.$q.loading.show()
        }
        return this.$SowellProxy
          .issuesByResidenceId(
            residenceId,
            this.options.skip,
            this.options.limit
          )
          .then((response) => {
            this.handleIssuesResponse(response, loadMore)
          })
          .catch((err) => {
            console.log(err)
          })
          .finally(() => {
            if (!loadMore) {
              this.$q.loading.hide()
            }
            if (done) {
              done()
            }
          })
      } else {
        if (done) {
          done()
        }
      }
    },
    handleIssuesResponse(response, loadMore) {
      const isNotFirstPage = this.options.skip > this.options.limit
      if (loadMore && isNotFirstPage) {
        this.otherIssues = [
          ...this.otherIssues,
          ...this.formatObjectIssues(response)
        ]
      } else {
        this.otherIssues = this.formatObjectIssues(response)
      }

      if (response.length === 0 || response.length < this.options.limit) {
        this.otherLoaded = true
      }

      this.options.skip += this.options.limit
    },
    getIssues(value, loadMore, done) {
      switch (this.filterMode) {
        case 'residence':
          this.getIssuesByResidenceId(Number(value), loadMore, done)
          break
        case 'place':
          this.getIssuesByPlaceId(Number(value), loadMore, done)
          break
      }
    },
    formatObjectIssues(issues) {
      return issues.map((issue) => {
        let formatedIssue = {
          meta: {
            id: issue.id,
            disturbance: issue.disturbance,
            isPrivate: issue['is-private'],
            isRequestTenant: issue['is-request-tenant'],
            createdAt: issue['created-at'],
            updatedAt: issue['updated-at']
          },
          location: {
            place: {
              name: issue.location.place
            },
            residence: {
              name: issue.location.zone
            },
            spot: {
              name: issue.location.spot
            }
          },
          content: {
            message: issue.message,
            imgsUrls: issue.imgsUrls
          },
          tracking: {
            status: issue.status,
            talks: issue.talks,
            code: issue.code
          },
          author: {
            id: Number(issue.author.id),
            firstName: issue.author['first-name'],
            lastName: issue.author['last-name']
          },
          category: {
            id: issue.category.id,
            name: issue.category.name
          },
          fromApi: true
        }
        if (issue.checkpoint && issue.checkpoint.question) {
          // NOTE: la question doit exister pour être sûr que le checkpoint existe vraiment
          let reportRelatedInfo = {
            checklist: issue.checkpoint.checklist
              ? {
                  id: issue.checkpoint.checklist.id,
                  name: issue.checkpoint.checklist.name
                }
              : undefined,
            checkpoint: {
              id: issue.checkpoint.id,
              name: issue.checkpoint.question
            }
          }
          Object.assign(formatedIssue.tracking, { report: reportRelatedInfo })
        }

        if (issue.reason) {
          let issueReasonInfo = {
            id: issue.reason.id,
            name: issue.reason.name
          }
          Object.assign(formatedIssue.category, { reason: issueReasonInfo })
        }
        return formatedIssue
      })
    }
  },
  watch: {
    search: function (term) {
      this.filterIssues(term)
    },
    filterValue: function (newValue) {
      this.options = { limit: LIMIT, skip: 0 }
      if (newValue) {
        this.otherIssues = []
        switch (this.filterMode) {
          case 'residence':
            this.getIssuesByResidenceId(Number(this.filterValue), false)
            break
          case 'place':
            this.getIssuesByPlaceId(Number(this.filterValue), false)
            break
        }
      }
    },
    isLoadingIssues: function (value) {
      if (!value) {
        // NOTE: on a fini le chargement de tout les issues
        this.initIssues()
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.separator
  border-bottom: 1px dotted grey
.q-item
  // padding 15px 5px
.q-item-main
  margin-left 10px
  max-height 82px
  overflow hidden
.q-item-sublabel
  overflow: hidden
  max-width: 90%
  white-space: nowrap
  text-overflow: ellipsis
.comment
  overflow: hidden
  display: -webkit-box
  -webkit-line-clamp: 2
  -webkit-box-orient: vertical
  font-size: 15px
  padding: 5px 0px
  quotes: "\201C""\201D""\2018""\2019"
.comment::before
  content: open-quote
  display: inline
  height: 0
  line-height: 0
  left: 0px
  position: relative
  top: 5px
  color: #000
  font-size: 1.3em
.comment::after
  content: close-quote
  display: inline
  height: 0
  line-height: 0
  left: 0px
  position: relative
  top: 5px
  color: #000
  font-size: 1.3em
.q-chip-dense
  font-size 9px
  padding 2px 5px
  font-weight 600
.q-chip-floating
  left: -0.5em
  right: auto
  top: -0.5em

.root {
  height: calc(100vh - 215px);
  top: 112px;
  position: relative
}
.otherRoot {
  height: calc(100vh - 175px);
  top: 72px;
  position: relative
}
.rootWithoutTabs {
  height: calc(100vh - 143px);
  position: relative
  top: 40px
}
</style>
